'use client'

import { useEffect } from 'react'
import Link from 'next/link'

interface ErrorPageProps {
  error: Error & { digest?: string }
  reset: () => void
}

const isChunkLoadError = (error?: { name?: string; message?: string }) =>
  error?.name === 'ChunkLoadError' ||
  /ChunkLoadError|Loading chunk [\w-]+ failed|Failed to load chunk/i.test(error?.message ?? '')

export default function ErrorPage({ error, reset }: ErrorPageProps) {
  // RUNTEST/chunk recovery: a stale-deploy chunk error that crashes a route
  // bubbles here. A one-time hard reload fetches the fresh bundle; the
  // sessionStorage guard prevents a reload loop on a genuinely broken build.
  useEffect(() => {
    if (isChunkLoadError(error) && !sessionStorage.getItem('pv-chunk-reload')) {
      sessionStorage.setItem('pv-chunk-reload', '1')
      window.location.reload()
    }
  }, [error])

  return (
    <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 text-center">
      <h1 className="font-[family-name:var(--font-heading)] text-2xl font-bold text-[var(--color-text-primary)] mb-2">
        Something went wrong
      </h1>
      <p className="text-[var(--color-text-secondary)] text-sm mb-8 max-w-md">
        Our team has been notified. Please try again.
      </p>
      <div className="flex items-center gap-3">
        <button
          onClick={reset}
          className="rounded-[var(--radius-md)] bg-[var(--color-brand-indigo)] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--color-brand-violet)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-indigo)]"
        >
          Try again
        </button>
        <Link
          href="/"
          className="rounded-[var(--radius-md)] px-5 py-2.5 text-sm font-medium text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-indigo)]"
        >
          Go home
        </Link>
      </div>
      {error.digest && (
        <p className="mt-6 text-xs text-[var(--color-text-muted)]">
          Reference: {error.digest}
        </p>
      )}
    </div>
  )
}
